import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  CxoneAgentEventInput,
  CxoneAgentSessionLease,
} from '@servicesai/api-client';
import { admin, api, getStaffToken, STAFF_TOKEN_CHANGED_EVENT } from '../lib/api.js';
import { cxoneSdkBridge } from './sdkBridge.js';
import type { CxoneSdkContact, CxoneSdkSnapshot } from './types.js';

const INITIAL_SNAPSHOT: CxoneSdkSnapshot = {
  status: 'loading',
  config: null,
  identity: null,
  contacts: [],
  selectedContactId: null,
  remoteTypingContactIds: [],
};

interface CxoneAgentContextValue extends CxoneSdkSnapshot {
  selectedContact: CxoneSdkContact | null;
  sdkAuthoritative: boolean;
  degradedFallback: boolean;
  login(): Promise<void>;
  logout(): Promise<void>;
  completeAuthentication(code: string, state: string): Promise<void>;
  selectContact(contactId: string | null): void;
  reply(contactId: string, text: string): Promise<void>;
  uploadAttachment(contactId: string, file: File, caption?: string): Promise<void>;
  setTyping(contactId: string, typing: boolean): Promise<void>;
  release(contactId: string): Promise<void>;
  transfer(contactId: string, routingQueueId: string): Promise<void>;
  resolve(contactId: string, dispositionId?: number): Promise<void>;
  refresh(): Promise<void>;
}

const CxoneAgentContext = createContext<CxoneAgentContextValue | null>(null);

export function CxoneAgentProvider({ children }: { children: ReactNode }) {
  const [snapshot, setSnapshot] = useState(INITIAL_SNAPSHOT);
  const leaseRef = useRef<CxoneAgentSessionLease | null>(null);
  const initializedRef = useRef(false);
  const initializeGenerationRef = useRef(0);
  const authorityQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const pendingAuthorityEventsRef = useRef<CxoneAgentEventInput[]>([]);

  const mirrorAuthorityEvent = useCallback((event: CxoneAgentEventInput) => {
    const lease = leaseRef.current;
    if (!lease) {
      pendingAuthorityEventsRef.current = [
        ...pendingAuthorityEventsRef.current.slice(-199),
        event,
      ];
      return;
    }
    authorityQueueRef.current = authorityQueueRef.current
      .catch(() => undefined)
      .then(() => admin().cxoneAgentEvent(event, lease.leaseToken))
      .catch((error) => {
        console.warn('[cxone] authority event mirror failed', error);
      });
  }, []);

  const establishLease = useCallback(async (next: CxoneSdkSnapshot) => {
    if (next.status !== 'ready' || !next.identity?.agentId || leaseRef.current) return;
    await admin().cxoneLinkAgent(next.identity.agentId);
    leaseRef.current = await admin().cxoneStartAgentSession();
    const pending = pendingAuthorityEventsRef.current.splice(0);
    for (const event of pending) mirrorAuthorityEvent(event);
  }, [mirrorAuthorityEvent]);

  const initialize = useCallback(async () => {
    if (initializedRef.current) return;
    if (!getStaffToken()) {
      setSnapshot({ ...INITIAL_SNAPSHOT, status: 'not_configured' });
      return;
    }
    initializedRef.current = true;
    const generation = ++initializeGenerationRef.current;
    try {
      const config = await admin().cxoneAgentConfig();
      if (generation !== initializeGenerationRef.current) return;
      await cxoneSdkBridge.initialize(config, {
        onSnapshot(next) {
          if (generation !== initializeGenerationRef.current) return;
          setSnapshot(next);
          void establishLease(next).catch((error) => {
            setSnapshot((current) => ({
              ...current,
              status: 'degraded',
              error: error instanceof Error ? error.message : String(error),
            }));
          });
        },
        onAuthorityEvent: mirrorAuthorityEvent,
      });
    } catch (error) {
      if (generation !== initializeGenerationRef.current) return;
      setSnapshot((current) => ({
        ...current,
        status: 'error',
        error: error instanceof Error ? error.message : String(error),
      }));
      initializedRef.current = false;
    }
  }, [establishLease, mirrorAuthorityEvent]);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const payload =
        event.data && typeof event.data === 'object'
          ? (event.data as Record<string, unknown>)
          : {};
        if (
          payload.type !== 'servicesai.cxone.auth' ||
          typeof payload.code !== 'string' ||
          typeof payload.state !== 'string'
        ) {
        return;
      }
        void cxoneSdkBridge.completeAuthentication(payload.code, payload.state);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const lease = leaseRef.current;
      if (!lease) return;
      void admin()
        .cxoneHeartbeatAgentSession(
          lease.id,
          lease.leaseToken,
          snapshot.status === 'degraded' || snapshot.status === 'error',
        )
        .then((result) => {
          leaseRef.current = { ...lease, expiresAt: result.expiresAt };
        })
        .catch((error) => {
          leaseRef.current = null;
          setSnapshot((current) => ({
            ...current,
            status: 'degraded',
            error: error instanceof Error ? error.message : String(error),
          }));
        });
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [snapshot.status]);

  useEffect(
    () => () => {
      const lease = leaseRef.current;
      if (lease) {
        void admin().cxoneEndAgentSession(lease.id, lease.leaseToken).catch(() => undefined);
        leaseRef.current = null;
      }
    },
    [],
  );

  const login = useCallback(() => cxoneSdkBridge.login('page'), []);
  const logout = useCallback(async () => {
    const lease = leaseRef.current;
    pendingAuthorityEventsRef.current = [];
    if (lease) {
      await admin().cxoneEndAgentSession(lease.id, lease.leaseToken).catch(() => undefined);
      leaseRef.current = null;
    }
    await cxoneSdkBridge.logout();
  }, []);

  const refresh = useCallback(async () => {
    initializedRef.current = false;
    await initialize();
  }, [initialize]);

  useEffect(() => {
    const onStaffTokenChanged = (event: Event) => {
      const lease = leaseRef.current;
      leaseRef.current = null;
      pendingAuthorityEventsRef.current = [];
      initializeGenerationRef.current += 1;
      const previousToken =
        event instanceof CustomEvent &&
        event.detail &&
        typeof event.detail.previousToken === 'string'
          ? event.detail.previousToken
          : '';
      const endPrevious =
        lease && previousToken
          ? api
              .admin(previousToken)
              .cxoneEndAgentSession(lease.id, lease.leaseToken)
              .catch(() => undefined)
          : Promise.resolve();
      void endPrevious.finally(() => refresh());
    };
    window.addEventListener(STAFF_TOKEN_CHANGED_EVENT, onStaffTokenChanged);
    return () =>
      window.removeEventListener(STAFF_TOKEN_CHANGED_EVENT, onStaffTokenChanged);
  }, [refresh]);

  const value = useMemo<CxoneAgentContextValue>(() => {
    const selectedContact =
      snapshot.contacts.find((contact) => contact.id === snapshot.selectedContactId) ?? null;
    return {
      ...snapshot,
      selectedContact,
      sdkAuthoritative:
        snapshot.config?.mode === 'live' &&
        snapshot.config.connectionStatus === 'live' &&
        snapshot.status === 'ready',
      degradedFallback:
        snapshot.config?.mode !== 'live' ||
        snapshot.config.connectionStatus === 'degraded' ||
        snapshot.status === 'degraded',
      login,
      logout,
      completeAuthentication: (code, state) =>
        cxoneSdkBridge.completeAuthentication(code, state),
      selectContact: (contactId) => cxoneSdkBridge.selectContact(contactId),
      reply: (contactId, text) => cxoneSdkBridge.reply(contactId, text),
      uploadAttachment: (contactId, file, caption) =>
        cxoneSdkBridge.uploadAttachment(contactId, file, caption),
      setTyping: (contactId, typing) => cxoneSdkBridge.setTyping(contactId, typing),
      release: (contactId) => cxoneSdkBridge.release(contactId),
      transfer: (contactId, routingQueueId) =>
        cxoneSdkBridge.transfer(contactId, routingQueueId),
      resolve: (contactId, dispositionId) =>
        cxoneSdkBridge.resolve(contactId, dispositionId),
      refresh,
    };
  }, [snapshot, login, logout, refresh]);

  return (
    <CxoneAgentContext.Provider value={value}>
      {children}
    </CxoneAgentContext.Provider>
  );
}

export function useCxoneAgent(): CxoneAgentContextValue {
  const value = useContext(CxoneAgentContext);
  if (!value) throw new Error('useCxoneAgent must be used inside CxoneAgentProvider');
  return value;
}
